// Copyright (c) Docugami, Inc. All rights reserved.**/

import axios from "axios";
import { Config } from "config";
import { useEffect } from "react";
import { reportsApiSlice, useGetAbstractPreviewExistsQuery } from "store/reports/reportsSlice";
import { RequestState, useAsyncHelper } from "./AsyncHelper";
import { useAppDispatch, useAppSelector } from "./redux";
import { selectIsReportPublished } from "store/reports/selectors";

export interface AbstractPdfViewProps {
    folderId: string;
    reportId: string;
    isReportPublished: boolean;
}

export interface AbstractPdfView {
    abstractPreviewUrl: string;
    isLoading: boolean;
    isReady: boolean;
}

export const useAbstractPreview = ({ folderId, reportId, isReportPublished }: AbstractPdfViewProps): AbstractPdfView => {
    const isPublished = useAppSelector(selectIsReportPublished);

    const abstractPreviewExists = useGetAbstractPreviewExistsQuery({ reportId, folderId });
    const { execute: executeDownload, state: downloadStatus, data: inMemoryBlobUrl } = useAsyncHelper<string>("Download Abstract Preview", folderId);

    const arePublishedStatesInitialized = isPublished !== undefined && abstractPreviewExists.data !== undefined

    const isPublishedPreviewAvailable = !!abstractPreviewExists.data;
    const dispatch = useAppDispatch();

    useEffect(() => { dispatch(reportsApiSlice.util.invalidateTags(['AbstractPreview'])) }, [isReportPublished, dispatch]);

    useEffect(() => () => {
        if (!!inMemoryBlobUrl) {
            URL.revokeObjectURL(inMemoryBlobUrl)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const isDownloadNeeded = !inMemoryBlobUrl &&
            downloadStatus !== RequestState.InProgress &&
            downloadStatus !== RequestState.Error;

        if (arePublishedStatesInitialized &&
            isDownloadNeeded) {
            const url = !isPublishedPreviewAvailable || !isPublished ?
                `${Config.reportsApiUrl}/stream/reports/${encodeURIComponent(reportId)}/resources/${encodeURIComponent(folderId)}/abstract-preview/on-demand` :
                `${Config.reportsApiUrl}/stream/reports/${encodeURIComponent(reportId)}/resources/${encodeURIComponent(folderId)}/abstract-preview`;

            executeDownload((async () => {
                const response = await axios.request({
                    method: 'GET',
                    url,
                    responseType: "blob"
                });

                return !!response.data ? URL.createObjectURL(response.data) : ''
            })());
        }
    }, [downloadStatus, executeDownload, folderId, isPublished, inMemoryBlobUrl, reportId, isPublishedPreviewAvailable, arePublishedStatesInitialized]);

    return {
        abstractPreviewUrl: !abstractPreviewExists.isFetching ? inMemoryBlobUrl ?? '' : '',
        isLoading: downloadStatus === RequestState.InProgress || abstractPreviewExists.isFetching,
        isReady: !!inMemoryBlobUrl,
    }
}
